// The first screen, for both kinds: one card that never moves. The toggle changes the option
// row, the button and the one line beside it, and nothing else. Pure rules, no React.
import { reelQuote, type ReelRequest } from '@str/shared';
import { SHAPES, lineTooLong, type Kind, type Shape } from './wizard';

export type Fidelity = ReelRequest['fidelity'];
export type Look = { id: Fidelity; label: string; blurb: string };

/** The three treatments the storyboard takes, grounded first. */
export const LOOKS: Look[] = [
  { id: 'original', label: 'Close to my photos', blurb: 'Your place as it is' },
  { id: 'balanced', label: 'A cinematic touch', blurb: 'More light and cropping' },
  { id: 'enhanced', label: 'Fully enhanced', blurb: 'Bolder color and framing' },
];
export const REEL_SECONDS = [15, 30] as const;

export type MomentAction = {
  label: string;
  /** The one line beside the button. */
  note: string;
  /** What the button spends now; a post looks for photos for free. */
  cost: number;
  short: boolean;
  disabled: boolean;
};

type Wallet = { balance: number } | null;

/**
 * What the one button does and says. A post goes on to its photos for free and the line says
 * what Make it will spend at the end. A reel builds its storyboard now, for the quoted price,
 * and the line says what filming adds once the scenes are approved.
 */
export function momentAction(
  kind: Kind,
  ctx: {
    line: string;
    credits: Wallet;
    spend?: number;
    seconds?: 15 | 30;
    /** The cards Make it will draw, once the photos are chosen; `spend` is then their total. */
    cards?: number;
  },
): MomentAction {
  const words = !!ctx.line.trim() && !lineTooLong(ctx.line);
  if (kind === 'Post') {
    const spend = ctx.spend ?? 0;
    const cards = ctx.cards;
    // Before the photos the price is a picture's; after, the total, and a card's when there are several.
    const price = !cards
      ? `${spend} ${plural(spend)} a picture`
      : cards > 1
        ? `${spend} ${plural(spend)} (${spend / cards} a card)`
        : `${spend} ${plural(spend)}`;
    const line = `Free to find your photos · Make it spends ${price}`;
    return {
      label: 'Find my photos',
      note: ctx.credits ? `${line} · you have ${ctx.credits.balance}` : line,
      cost: 0,
      short: false,
      disabled: !words,
    };
  }
  const seconds = ctx.seconds ?? 15;
  const cost = reelQuote('storyboard', seconds).credits;
  const film = reelQuote('video', seconds).credits;
  const short = !!ctx.credits && ctx.credits.balance < cost;
  const line = `${cost + film} in all: ${cost} now for the storyboard, ${film} to film it once you approve the scenes`;
  return {
    label: short ? `Get credits · ${cost} needed` : `Build the storyboard · ${cost} credits`,
    note: short
      ? `Needs ${cost} credits · you have ${ctx.credits!.balance}`
      : ctx.credits
        ? `${line} · you have ${ctx.credits.balance}`
        : line,
    cost,
    short,
    disabled: !words,
  };
}

/** A post's option row: the shapes, Your idea first. */
export function momentShapes(): Shape[] {
  const idea = SHAPES.find((s) => s.id === 'idea');
  return idea ? [idea, ...SHAPES.filter((s) => s !== idea)] : SHAPES;
}

const plural = (n: number) => (n === 1 ? 'credit' : 'credits');
